import { Button } from "@Light/components/page/Button";
import { PageBody } from "@Light/components/page/PageBody";
import { Field } from "@Light/components/system/Fieldset";
import { Input } from "@Light/components/system/Input";
import { useScaffold } from "@Light/scaffold";
import { useLight } from "@Light/services/light";
import type { EligibilityResponse, Utility } from "@Light/services/lightTypes";
import { zip5 } from "@Light/utils/address";
import { Lightning01, MarkerPin01 } from "@untitledui/icons";
import { useState } from "react";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { PartnerTosDisclaimer } from "../PartnerTosDisclaimer";
import { ServiceLocationSheets } from "../address/ServiceLocationSheets";
import { utilityAndPostalCodeToRequirementsPatch } from "../address/patch";
import { useServiceLocationSheets } from "../address/useServiceLocationSheets";
import { useEnrollment } from "../flow/context";
import { useBrokerTermsAgreement } from "../useBrokerTermsAgreement";
import { ZipUtilityIneligibleBottomSheet } from "./ZipUtilityIneligibleBottomSheet";
import { ZipUtilitySplitBottomSheet } from "./ZipUtilitySplitBottomSheet";
import { isEligible } from "./isEligible";

export const ZipUtilitySearch = () => {
  const { requirements, next, save } = useEnrollment();
  const { useLazyCheckEligibilityQuery } = useLight();
  const [checkEligibility, { isFetching, isError }] =
    useLazyCheckEligibilityQuery();

  const existing = requirements.service_address?.value?.postal_code;
  const [postalCode, setPostalCode] = useState(existing ? zip5(existing) : "");
  const [eligibility, setEligibility] = useState<EligibilityResponse>();
  const ineligible = eligibility !== undefined && !isEligible(eligibility);
  const splitUtilities =
    eligibility && isEligible(eligibility) && eligibility.utilities.length > 1
      ? eligibility.utilities
      : [];

  const brokerTermsAgreement = useBrokerTermsAgreement();
  const sheets = useServiceLocationSheets({
    postalCode,
    additionalRequirements: brokerTermsAgreement,
  });

  const advance = (zip: string, utility: Utility) =>
    next({
      ...utilityAndPostalCodeToRequirementsPatch(requirements, zip, utility.id),
      ...brokerTermsAgreement,
    });

  const onSubmit = async (zip: string) => {
    setPostalCode(zip);
    const result = await checkEligibility({ postal_code: zip })
      .unwrap()
      .catch(() => undefined);
    if (!result) return;

    // Immediately patch ineligible zips for data consistency, and to remember
    // it for them if they return.
    if (!isEligible(result)) {
      const patch = utilityAndPostalCodeToRequirementsPatch(requirements, zip);
      if (patch) void save(patch).catch(() => undefined);
    } else if (result.utilities.length === 1) {
      await advance(zip, result.utilities[0]);
      return;
    }

    setEligibility(result);
  };

  return (
    <>
      <BaseZipUtilitySearch
        postalCode={postalCode}
        onPostalCodeChange={setPostalCode}
        isLoading={isFetching}
        isError={isError}
        onSubmit={(zip) => {
          void onSubmit(zip);
        }}
      />
      <ZipUtilityIneligibleBottomSheet
        open={ineligible}
        postalCode={postalCode}
        onClose={() => {
          setEligibility(undefined);
        }}
      />
      <ZipUtilitySplitBottomSheet
        open={splitUtilities.length > 0 && !sheets.activeSheet}
        onClose={() => {
          setEligibility(undefined);
        }}
        postalCode={postalCode}
        utilities={splitUtilities}
        onSubmit={(utility) => {
          void advance(postalCode, utility);
        }}
        onClickProvideAddress={() => {
          sheets.openSheet("address");
        }}
      />
      <ServiceLocationSheets {...sheets} />
    </>
  );
};

export type BaseZipUtilitySearchProps = {
  postalCode: string;
  onPostalCodeChange: (postalCode: string) => void;
  onSubmit: (postalCode: string) => void;
  isLoading?: boolean;
  isError?: boolean;
};

export const BaseZipUtilitySearch = ({
  postalCode,
  onPostalCodeChange,
  onSubmit,
  isLoading = false,
  isError = false,
}: BaseZipUtilitySearchProps) => {
  const scaffold = useScaffold();
  const ErrorMessage = scaffold.system.errorMessage;

  return (
    <form
      className="h-full"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(postalCode);
      }}
    >
      <PageBody
        centered
        icon={Lightning01}
        title="Let’s find your electricity plan. What’s your zip code?"
        footer={
          <div className="flex flex-col gap-3">
            <EnrollHelpButtonFooter>
              <Button
                size="lg"
                className="w-full"
                type="submit"
                disabled={isLoading}
                data-testid="zip-utility-search-submit"
              >
                View offer
              </Button>
            </EnrollHelpButtonFooter>
            <PartnerTosDisclaimer className="text-center" />
          </div>
        }
      >
        <Field className="flex flex-col gap-1.5">
          <Input
            icon={MarkerPin01}
            value={postalCode}
            onChange={(e) => {
              onPostalCodeChange(e.target.value);
            }}
            isError={isError}
            inputMode="numeric"
            autoComplete="postal-code"
            required
            pattern="[0-9]{5}"
            maxLength={5}
            placeholder="76710"
            aria-label="Zip code"
            data-record="true"
            data-testid="zip-utility-search-input"
          />
          {isError ? (
            <ErrorMessage>
              We couldn&rsquo;t check that zip code. Please try again.
            </ErrorMessage>
          ) : null}
        </Field>
      </PageBody>
    </form>
  );
};
